import "chartjs-adapter-date-fns";
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip,
} from "chart.js";
import { DateSetMode, MS_IN_DAY, setDateToNextWeekday, WeekDay } from "./chartutils";

Chart.register(
    TimeScale,
    LinearScale,
    LineController,
    PointElement,
    LineElement,
    BarController,
    CategoryScale,
    BarElement,
    Tooltip,
    Legend
);

type ChartDataPoint = { x: number; y: number };
type ChartDisplayData = {
    readonly minBuyout: ChartDataPoint[];
    readonly minVal: number;
    readonly maxVal: number;
    readonly dailyAvg: ChartDataPoint[];
    readonly weeklyAvg: ChartDataPoint[];
    readonly quantity: ChartDataPoint[];
    readonly wednesDays: ChartDataPoint[]; // my dudes
};

/**
 * Accumulate daily data and return data points for daily averages.
 */
class DailyAvgBuilder {
    private static readonly halfDay = MS_IN_DAY * 0.5;
    private dailySum = 0;
    private dailyCount = 0;
    private finalized = false;
    private readonly endCurrentDay = new Date(0);
    private readonly dataPoints: ChartDataPoint[] = [];

    private saveAvgDataPoint() {
        if (this.dailyCount > 0) {
            this.dataPoints.push({
                x: this.endCurrentDay.getTime() - DailyAvgBuilder.halfDay,
                y: this.dailySum / this.dailyCount,
            });
        }
    }

    private setNewDay(timestamp: number) {
        this.endCurrentDay.setTime(timestamp);
        this.endCurrentDay.setHours(24, 0, 0, 0);
        this.dailyCount = 0;
        this.dailySum = 0;
    }

    /**
     * Add a price data point.
     * @param timestamp
     * @param value
     */
    addDataPoint(timestamp: number, value: number) {
        if (this.finalized) throw new Error("Already finalized!");
        if (timestamp > this.endCurrentDay.getTime()) {
            this.saveAvgDataPoint();
            this.setNewDay(timestamp);
        }
        this.dailySum += value;
        this.dailyCount++;
    }

    /**
     * Returns daily average data points.
     * @returns
     */
    finalize() {
        if (!this.finalized) {
            // if (this.dailyCount > 0) this.saveAvgDataPoint();
            this.finalized = true;
        }
        return this.dataPoints;
    }
}

/**
 * Accumulate weekly data and return data points for weekly averages.
 */
class WeeklyAvgBuilder {
    private static readonly halfWeek = MS_IN_DAY * 0.5 * 7;
    private static readonly week = MS_IN_DAY * 7;
    private weeklySum = 0;
    private weeklyCount = 0;
    private finalized = false;
    private readonly endCurrentWeek = new Date(0);
    private readonly dataPoints: ChartDataPoint[] = [];

    private saveAvgDataPoint() {
        if (this.weeklyCount > 0) {
            this.dataPoints.push({
                x: this.endCurrentWeek.getTime() - WeeklyAvgBuilder.halfWeek,
                y: this.weeklySum / this.weeklyCount,
            });
        }
    }

    private setNewWeek(timestamp: number) {
        this.endCurrentWeek.setTime(timestamp + WeeklyAvgBuilder.week);
        this.endCurrentWeek.setHours(24, 0, 0, 0);
        const curDay = this.endCurrentWeek.getDay();
        this.endCurrentWeek.setDate(this.endCurrentWeek.getDate() + (3 - curDay));
        this.weeklyCount = 0;
        this.weeklySum = 0;
    }

    /**
     * Add a price data point.
     * @param timestamp
     * @param value
     */
    addDataPoint(timestamp: number, value: number) {
        if (this.finalized) throw new Error("Already finalized!");
        if (timestamp > this.endCurrentWeek.getTime()) {
            this.saveAvgDataPoint();
            this.setNewWeek(timestamp);
        }
        this.weeklySum += value;
        this.weeklyCount++;
    }

    /**
     * Returns daily average data points.
     * @returns
     */
    finalize() {
        if (!this.finalized) {
            // if (this.weeklyCount > 0) this.saveAvgDataPoint();
            this.finalized = true;
        }
        return this.dataPoints;
    }
}

/**
 * Create data points for all wednesdays in time frame.
 * @param minTime The starting time.
 * @param maxTime The ending time.
 * @param topVal Max value in chart, used to size bars that mark the days.
 * @returns
 */
function createWednesDayPoints(minTime: number, maxTime: number, topVal: number): ChartDataPoint[] {
    const wednesDays: ChartDataPoint[] = [];
    const wednesday = setDateToNextWeekday(new Date(minTime), WeekDay.Wed, DateSetMode.StartOfDay);
    // TODO: Fix in setDateToNextWeekday()
    if (wednesday.getTime() < minTime) wednesday.setDate(wednesday.getDate() + 7);
    while (wednesday.getTime() < maxTime) {
        wednesDays.push({ x: wednesday.getTime(), y: topVal });
        wednesday.setDate(wednesday.getDate() + 7);
    }
    wednesDays.reverse();
    return wednesDays;
}

/**
 * Creates data for display in history chart.
 * @param data
 * @param minTime The time to start showing data from.
 * @returns
 */
export function createChartData(data: ApiAuctionItemData[], minTime: number): ChartDisplayData {
    // TODO: Data is soreted at this point, but maybe shouldn't trust that and do it here instead?

    minTime = data[0] ? Math.max(minTime, data[0].timestamp) : Date.now();
    const maxTime = data[data.length - 1]?.timestamp ?? minTime - 1;

    const minBuyoutData: ChartDataPoint[] = [];
    let minVal = Number.MAX_VALUE;
    let maxVal = 0;
    const quantityData: ChartDataPoint[] = [];
    const dailyAvgBuilder = new DailyAvgBuilder();
    const weeklyAvgBuilder = new WeeklyAvgBuilder();

    for (const entry of data) {
        if (entry.timestamp < minTime) continue;
        const shownValue = entry.minBuyout * 0.0001;
        minBuyoutData.push({ x: entry.timestamp, y: shownValue });
        if (minVal > shownValue) minVal = shownValue;
        if (maxVal < shownValue) maxVal = shownValue;
        quantityData.push({ x: entry.timestamp, y: entry.quantity });
        dailyAvgBuilder.addDataPoint(entry.timestamp, shownValue);
        weeklyAvgBuilder.addDataPoint(entry.timestamp, shownValue);
    }

    const chartData: ChartDisplayData = {
        minBuyout: minBuyoutData,
        minVal: minVal,
        maxVal: maxVal,
        dailyAvg: dailyAvgBuilder.finalize(),
        weeklyAvg: weeklyAvgBuilder.finalize(),
        quantity: quantityData,
        wednesDays: createWednesDayPoints(minTime, maxTime, maxVal),
    };

    return chartData;
}

function chartMinValue(min: number) {
    return min - 0.01;
}

/**
 * Update existing chart with new data.
 * @param chart
 * @param chartData
 */
export function updateChart(chart: Chart, chartData: ChartDisplayData): void {
    chart.options.scales!.y!.min = chartMinValue(chartData.minVal);
    chart.data.datasets[0]!.data = chartData.minBuyout;
    chart.data.datasets[1]!.data = chartData.dailyAvg;
    chart.data.datasets[2]!.data = chartData.weeklyAvg;
    chart.data.datasets[3]!.data = chartData.quantity;
    chart.data.datasets[4]!.data = chartData.wednesDays;
    chart.update();
}

const LINE_COLOR = "#424242";
const BORDER_COLOR = "#606060";
const FONT_COLOR_LABELS = "#f0f0f0";

/**
 * Create new price history chart.
 * @param chartData
 * @param canvas
 * @returns
 */
export function createChart(chartData: ChartDisplayData, canvas: HTMLCanvasElement): Chart {
    return new Chart(canvas, {
        type: "line",
        options: {
            parsing: false,
            responsive: true,
            scales: {
                x: {
                    type: "time",
                    time: {
                        unit: "day",
                        tooltipFormat: "yyyy-MM-dd HH:mm eee",
                    },
                    ticks: {
                        color: FONT_COLOR_LABELS,
                    },
                    grid: {
                        color: LINE_COLOR,
                    },
                    border: {
                        color: BORDER_COLOR,
                    },
                },
                y: {
                    min: chartMinValue(chartData.minVal),
                    title: {
                        display: true,
                        text: "Gold",
                        color: FONT_COLOR_LABELS,
                    },
                    ticks: {
                        color: FONT_COLOR_LABELS,
                    },
                    grid: {
                        color: LINE_COLOR,
                    },
                    border: {
                        color: BORDER_COLOR,
                    },
                },
                yQuant: {
                    type: "linear",
                    position: "right",
                    title: {
                        display: true,
                        text: "Quanity",
                        color: FONT_COLOR_LABELS,
                    },
                    ticks: {
                        color: FONT_COLOR_LABELS,
                    },
                    grid: {
                        display: false,
                    },
                    border: {
                        color: BORDER_COLOR,
                    },
                },
            },
            plugins: {
                legend: {
                    position: "top",
                    labels: {
                        color: FONT_COLOR_LABELS,
                        boxWidth: 15,
                    },
                },
            },
        },
        data: {
            datasets: [
                {
                    label: "Min. Buyout",
                    data: chartData.minBuyout,
                    backgroundColor: "#00abff",
                    borderColor: "#00abff",
                    pointRadius: 2,
                    pointHoverRadius: 5,
                },
                {
                    label: "Daily Avg.",
                    data: chartData.dailyAvg,
                    backgroundColor: "#7d5a9a",
                    borderColor: "#7d5a9a",
                    pointRadius: 5,
                    pointHoverRadius: 7,
                },
                {
                    label: "Weekly Avg.",
                    data: chartData.weeklyAvg,
                    backgroundColor: "#d16d37ff",
                    borderColor: "#d16d37ff",
                    showLine: false,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                },
                {
                    label: "Quantity",
                    data: chartData.quantity,
                    yAxisID: "yQuant",
                    backgroundColor: "#629a42",
                    borderColor: "#629a42",
                    borderWidth: 1,
                    pointRadius: 1,
                    pointHoverRadius: 5,
                },
                {
                    label: "Wednesdays",
                    type: "bar",
                    data: chartData.wednesDays,
                    maxBarThickness: 2,
                    backgroundColor: "#b67f4f",
                    borderColor: "#b67f4f",
                },
            ],
        },
    });
}
